import { useState, useEffect } from 'react';
import { TrendingUp, Users, MessageSquare, Clock, BarChart3, RefreshCw, Eye } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Agent } from '@/types';
import {
  getAnalyticsSummary,
  getAnalyticsTrends,
  getResponseTimeDistribution,
  getHourlyDistribution,
  getTopTopics,
} from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface AgentAnalyticsProps {
  agent: Agent;
}

export function AgentAnalytics({ agent }: AgentAnalyticsProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [demoMode, setDemoMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Analytics data
  const [summary, setSummary] = useState<any>(null);
  const [trends, setTrends] = useState<any>(null);
  const [responseTime, setResponseTime] = useState<any>(null);
  const [hourly, setHourly] = useState<any>(null);
  const [topics, setTopics] = useState<any>(null);

  const periodDays = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summaryData, trendsData, responseTimeData, hourlyData, topicsData] = await Promise.all([
        getAnalyticsSummary(agent.agentId, periodDays, demoMode),
        getAnalyticsTrends(agent.agentId, periodDays, demoMode),
        getResponseTimeDistribution(agent.agentId, periodDays, demoMode),
        getHourlyDistribution(agent.agentId, Math.min(periodDays, 7), demoMode),
        getTopTopics(agent.agentId, periodDays, demoMode),
      ]);

      setSummary(summaryData);
      setTrends(trendsData);
      setResponseTime(responseTimeData);
      setHourly(hourlyData);
      setTopics(topicsData);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err instanceof Error ? err.message : '載入分析數據失敗');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [agent.agentId, timeRange, demoMode]);

  const refreshData = () => {
    fetchData();
  };

  // Calculate metrics for display
  const totalMessages = summary?.total_messages || 0;
  const activeUsers = summary?.active_users || 0;
  const avgResponseTime = summary?.avg_response_time_ms
    ? `${(summary.avg_response_time_ms / 1000).toFixed(1)}s`
    : '-';
  const avgMessagesPerDay = totalMessages > 0 ? Math.round(totalMessages / periodDays) : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-display font-semibold text-xl">分析數據</h3>
          <p className="text-sm text-muted-foreground">
            Agent 運營指標與統計分析
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Demo Mode Toggle */}
          <Button
            variant={demoMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDemoMode(!demoMode)}
            className={cn(demoMode && 'bg-purple-600 hover:bg-purple-700')}
          >
            <Eye className="h-4 w-4 mr-2" />
            {demoMode ? '展示模式' : '真實數據'}
          </Button>

          {/* Time Range Select */}
          <Select value={timeRange} onValueChange={(v) => setTimeRange(v as '7d' | '30d' | '90d')}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">近 7 天</SelectItem>
              <SelectItem value="30d">近 30 天</SelectItem>
              <SelectItem value="90d">近 90 天</SelectItem>
            </SelectContent>
          </Select>

          {/* Refresh Button */}
          <Button variant="outline" size="icon" onClick={refreshData} disabled={isLoading}>
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Demo Mode Badge */}
      {demoMode && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 text-sm">
          <Eye className="h-4 w-4" />
          <span>展示模式：顯示模擬數據，方便展示系統功能</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !summary && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Summary Cards */}
      {!isLoading && summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="總訊息數"
            value={totalMessages.toLocaleString()}
            change={demoMode ? '+12.5%' : '-'}
            changeUp={true}
            icon={<MessageSquare className="h-5 w-5" />}
            color="text-blue-500"
            bgColor="bg-blue-500/10"
          />
          <SummaryCard
            title="活躍用戶"
            value={activeUsers.toString()}
            change={demoMode ? '+8.2%' : '-'}
            changeUp={true}
            icon={<Users className="h-5 w-5" />}
            color="text-green-500"
            bgColor="bg-green-500/10"
          />
          <SummaryCard
            title="平均回應"
            value={avgResponseTime}
            change={demoMode ? '-5.3%' : '-'}
            changeUp={true}
            icon={<Clock className="h-5 w-5" />}
            color="text-purple-500"
            bgColor="bg-purple-500/10"
          />
          <SummaryCard
            title="日均訊息"
            value={avgMessagesPerDay.toString()}
            change={demoMode ? '+3.1%' : '-'}
            changeUp={true}
            icon={<BarChart3 className="h-5 w-5" />}
            color="text-orange-500"
            bgColor="bg-orange-500/10"
          />
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages Trend */}
        {trends?.daily_messages && (
          <Card>
            <CardHeader>
              <CardTitle>訊息趨勢</CardTitle>
              <CardDescription>每日訊息與用戶數量變化</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trends.daily_messages}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="messages"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.2}
                    name="訊息數"
                  />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="hsl(var(--status-running))"
                    fill="hsl(var(--status-running))"
                    fillOpacity={0.2}
                    name="用戶數"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Response Time Distribution */}
        {responseTime?.data && (
          <Card>
            <CardHeader>
              <CardTitle>回應時間分布</CardTitle>
              <CardDescription>訊息回應時間統計</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={responseTime.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis
                    dataKey="range"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: any, name: any) => [
                      name === 'percentage' ? `${value}%` : value,
                      name === 'count' ? '訊息數' : '百分比'
                    ]}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} name="訊息數" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth */}
        {trends?.user_growth && (
          <Card>
            <CardHeader>
              <CardTitle>用戶增長</CardTitle>
              <CardDescription>累計用戶與新增用戶趨勢</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trends.user_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                    name="總用戶"
                  />
                  <Line
                    type="monotone"
                    dataKey="new"
                    stroke="hsl(var(--status-running))"
                    strokeWidth={2}
                    dot={false}
                    name="新增用戶"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Hourly Distribution */}
        {hourly?.data && (
          <Card>
            <CardHeader>
              <CardTitle>時段分布</CardTitle>
              <CardDescription>各時段訊息量統計</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourly.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis
                    dataKey="hour"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    interval={3}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Bar dataKey="messages" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Top Topics */}
      {topics?.topics && (
        <Card>
          <CardHeader>
            <CardTitle>熱門對話主題</CardTitle>
            <CardDescription>用戶最常詢問的主題分類</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topics.topics.map((topic: any, index: number) => (
                <div key={topic.topic} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{topic.topic}</span>
                      <span className="text-sm text-muted-foreground">{topic.count} 則</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${(topic.count / (topics.topics[0]?.count || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <TrendBadge trend={topic.trend} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  title,
  value,
  change,
  changeUp,
  icon,
  color,
  bgColor,
}: {
  title: string;
  value: string;
  change: string;
  changeUp: boolean;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', bgColor)}>
            <span className={color}>{icon}</span>
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-display font-bold">{value}</p>
          </div>
          {change !== '-' && (
            <div className="text-right">
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  changeUp ? 'text-status-running border-status-running/30' : 'text-destructive border-destructive/30'
                )}
              >
                <TrendingUp className={cn('h-3 w-3 inline mr-1', !changeUp && 'rotate-180')} />
                {change}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TrendBadge({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  const config = {
    up: { label: '上升', className: 'text-status-running' },
    down: { label: '下降', className: 'text-destructive' },
    stable: { label: '持平', className: 'text-muted-foreground' },
  };

  const { label, className } = config[trend];

  return (
    <Badge variant="outline" className={cn('text-xs', className, 'border-current/30')}>
      {label}
    </Badge>
  );
}
